import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { confirmDialog } from "../components/ConfirmDialog";
import { applyBillingEnumText, durationUnitLabel, normalizeAccessCode, normalizeDependent, normalizeMaintenanceRequest } from "../utils/enums";
import Sidebar, { clearSession } from "../components/Sidebar";
import {
  Dashboard6Styles, InlineModal, Field, PagePreloader, Avatar, StatusChip, CardHead, PageHeader,
} from "../components/ModalUI";

// The primary-occupant API (create and update) stores only title, name, email, phone and photo.
// Gender, occupation and emergency contact aren't accepted yet, so they're hidden rather than
// silently dropped on save. Turn this on once the backend supports them.
const OCCUPANT_PROFILE_EXTRAS = false;

const DEV_PROXY_BASE = "/api";
const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = process.env.NODE_ENV === "development" ? DEV_PROXY_BASE : PROD_API_BASE;
const SESSION_KEY = "zangon_session";

const HOUSE_TYPE_IMAGES = {
  "1 Bedroom Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "1 Bedrooms Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "2 Bedroom Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "2 Bedrooms Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "3 Bedrooms Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "3 Bedroom Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "4 Bedrooms Duplex": "/assets/media/zangon/4bd.jpg",
  "4 Bedroom Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "4 Bedrooms Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "5 Bedrooms Terrace": "/assets/media/zangon/5-light.jpg",
  "6 Bedroom Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedrooms Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedroom Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
  "6 Bedrooms Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
};

function getCarImageUrl(make, model, year, color) {
  if (!make || !model) return null;
  return `https://cdn.imagin.studio/getimage?customer=img&make=${encodeURIComponent(make)}&modelFamily=${encodeURIComponent(model)}&modelYear=${year || 2024}&paintId=${encodeURIComponent(color || "Black")}&angle=23&zoomType=fullscreen`;
}

function getHouseImage(typeName) {
  if (!typeName) return "/assets/media/zangon/3bedroomsbungalo.jpg";
  const norm = typeName.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/bedrooms?/g, "bedroom");
  if (norm.includes("1bedroom") || norm.includes("1bd")) {
    return "/assets/media/zangon/1bedroombungalo.jpg";
  }
  if (norm.includes("2bedroom") || norm.includes("2bd")) {
    return "/assets/media/zangon/2bedroom.jpg";
  }
  if (norm.includes("3bedroombungalow") || norm.includes("3bedroombungalo") || norm.includes("3bd")) {
    return "/assets/media/zangon/3bedroomsbungalo.jpg";
  }
  if (norm.includes("4bedroombungalow") || norm.includes("4bedroombungalo")) {
    return "/assets/media/zangon/4bedroombungalo.jpg";
  }
  if (norm.includes("4bedroomduplex") || norm.includes("4bd")) {
    return "/assets/media/zangon/4bd.jpg";
  }
  if (norm.includes("5bedroom")) {
    const isDark = document.documentElement.getAttribute("data-bs-theme") === "dark" || 
                   localStorage.getItem("kt_theme_mode") === "dark";
    return isDark ? "/assets/media/zangon/5-dark.jpg" : "/assets/media/zangon/5-light.jpg";
  }
  if (norm.includes("6bedroombungalow")) {
    return "/assets/media/zangon/6bedroombungalow.jpg";
  }
  if (norm.includes("6bedroomterraceduplex") || norm.includes("6bedroomterraceduple") || (norm.includes("6bedroom") && norm.includes("terrace"))) {
    return "/assets/media/zangon/6bedroomterraceduple.jpg";
  }
  return HOUSE_TYPE_IMAGES[typeName] || "/assets/media/zangon/3bedroomsbungalo.jpg";
}

function getEmptyStateImg() {
  return "/assets/media/zangon/5-dark.jpg";
}

function getInitials(name) {
  if (!name || name === "—") return "?";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function toTitleCase(str) {
  if (!str) return '';
  return String(str).replace(/\w\S*/g, (word) =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
}

function safeJsonParse(text) {
  try { return applyBillingEnumText(JSON.parse(text)); } catch { return null; }
}

function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

function setSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function getAccessToken() { return getSession()?.accessToken || ""; }
export function getRefreshToken() { return getSession()?.refreshToken || ""; }

async function fetchWithTimeout(url, options = {}, timeoutMs = 60000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Try again.");
    throw err;
  } finally { clearTimeout(id); }
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || "Token refresh failed.");
  const prev = getSession() || {};
  const next = { ...prev, ...(json?.data ?? json ?? {}) };
  setSession(next);
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const renewed = await refreshAccessToken();
      token = renewed.accessToken;
      headers.Authorization = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired. Please sign in again.");
    }
  }
  return res;
}

async function uploadAdminImage(imageFile, adminId) {
  if (!imageFile || !adminId) return null;
  try {
    const formData = new FormData();
    formData.append("file", imageFile);
    const res = await authFetch(`${API_BASE}/admins/${adminId}/uploads`, {
      method: "POST",
      headers: {},
      body: formData,
    });
    const text = await res.text();
    const json = safeJsonParse(text);
    if (!res.ok) return null;
    if (typeof json?.data !== "string" || !json.data.startsWith("http")) return null;
    return json.data;
  } catch {
    return null;
  }
}

function parseArrayData(json) {
  if (!json) return [];
  const raw = json.data;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = safeJsonParse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.data)) return parsed.data;
  }
  if (Array.isArray(json)) return json;
  return [];
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateLong(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
}

const STATUS_META = {
  Created: { badge: "badge-light-danger", label: "Open" },
  Open: { badge: "badge-light-danger", label: "Open" },
  "In Progress": { badge: "badge-light-warning", label: "In Progress" },
  Resolved: { badge: "badge-light-success", label: "Resolved" },
  Closed: { badge: "badge-light-secondary", label: "Closed" },
};

function formatDependentType(type) {
  if (type === undefined || type === null) return "—";
  if (typeof type === "number") {
    switch (type) {
      case 0: return "Family Member";
      case 1: return "Staff";
      case 2: return "Child";
      case 3: return "Domestic Staff";
      case 4: return "Driver";
      case 5: return "Visitor (No Access Code)";
      default: return "Unknown";
    }
  }
  return String(type).replace(/([a-z])([A-Z])/g, "$1 $2");
}

export default function OccupantDetail() {
  const { tenantId, occupantId } = useParams();
  const [searchParams] = useSearchParams();
  const targetBuildingId = searchParams.get("buildingId") || "";
  const navigate = useNavigate();
  const session = getSession();
  const estateId = session?.estateId || "";

  const isUnassignedParam = occupantId === "unassigned" || occupantId === "primary" || occupantId === "new" || occupantId === "add" || occupantId === "none";

  const [pageReady, setPageReady] = useState(false);
  const [loading, setLoading] = useState(true);

  const [detail, setDetail] = useState(null);
  // Set when the occupant couldn't be loaded (network/server error), as opposed to a 404,
  // so a failed load isn't shown as "no occupant" with an Add button.
  const [loadError, setLoadError] = useState("");
  const [tenantDetail, setTenantDetail] = useState(null);
  const [dependents, setDependents] = useState([]);
  const [cars, setCars] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [assignedBuildings, setAssignedBuildings] = useState([]);

  const [assigning, setAssigning] = useState(false);
  const [unassigning, setUnassigning] = useState(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState("");

  const [selectedDependentForCode, setSelectedDependentForCode] = useState(null);
  const [codeIsPermanent, setCodeIsPermanent] = useState(true);
  const [codeDurationValue, setCodeDurationValue] = useState(1);
  const [codeDurationUnit, setCodeDurationUnit] = useState(0);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [dependentCodes, setDependentCodes] = useState([]);
  const [loadingDependentCodes, setLoadingDependentCodes] = useState(false);

  // Edit / Add modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    gender: "",
    occupation: "",
    emergencyContact: "",
    buildingId: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const editFileInputRef = useRef(null);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);

  // Bootstrap body setup
  useEffect(() => {
    const body = document.body;
    body.id = "kt_app_body";
    body.classList.add("app-default");
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");
    body.setAttribute("data-kt-app-sidebar-push-header", "true");
    body.setAttribute("data-kt-app-sidebar-push-toolbar", "true");
    body.setAttribute("data-kt-app-sidebar-push-footer", "true");
    const init = () => { window.KTThemeMode?.init?.(); window.KTComponents?.init?.(); };
    init();
    const t = setTimeout(init, 0);
    return () => clearTimeout(t);
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (editModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [editModalOpen]);

  const fetchAll = useCallback(async () => {
    if (!occupantId || !estateId || !tenantId) return;
    setLoading(true);
    setLoadError("");
    try {
      if (isUnassignedParam) {
        const bldgRes = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/details`);
        if (bldgRes.ok) {
          const bj = safeJsonParse(await bldgRes.text());
          const td = bj?.data || {};
          setTenantDetail(td);
          setAssignedBuildings(td.assignedBuildings || (td.assignedBuilding ? [td.assignedBuilding] : []));
        }
        setDetail(null);
        setDependents([]);
        setCars([]);
        setMaintenanceRequests([]);
      } else {
        const [detailRes, depRes, carsRes, mrRes, bldgRes] = await Promise.all([
          authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${occupantId}`),
          authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${occupantId}/dependents?pageIndex=1&pageSize=100`),
          authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${occupantId}/cars`),
          authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${occupantId}/maintenance-requests?pageIndex=1&pageSize=50`),
          authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/details`),
        ]);

        if (detailRes.ok) {
          const j = safeJsonParse(await detailRes.text());
          const d = j?.data;
          setDetail(d || null);
          if (d) {
            setEditForm({
              title: d.title || "",
              firstName: d.firstName || "",
              lastName: d.lastName || "",
              email: d.email || "",
              phoneNumber: d.phoneNumber || "",
              gender: d.gender || "",
              occupation: d.occupation || "",
              emergencyContact: d.emergencyContact || "",
              buildingId: d.currentAssignment?.buildingId || "",
            });
            if (d.imageUrl) setEditImagePreview(d.imageUrl);
          }
        } else if (detailRes.status === 404) {
          setDetail(null);
        } else {
          setLoadError(`Couldn't load this occupant (error ${detailRes.status}).`);
        }
        if (depRes.ok) setDependents(parseArrayData(safeJsonParse(await depRes.text())).map(normalizeDependent));
        if (carsRes.ok) {
          const j = safeJsonParse(await carsRes.text());
          setCars(Array.isArray(j?.data) ? j.data : parseArrayData(j));
        }
        if (mrRes.ok) setMaintenanceRequests(parseArrayData(safeJsonParse(await mrRes.text())).map(normalizeMaintenanceRequest));
        if (bldgRes.ok) {
          const bj = safeJsonParse(await bldgRes.text());
          const td = bj?.data || {};
          setTenantDetail(td);
          setAssignedBuildings(td.assignedBuildings || (td.assignedBuilding ? [td.assignedBuilding] : []));
        }
      }
    } catch (err) {
      if (!isUnassignedParam) {
        setLoadError("Couldn't load this occupant. Check your connection and try again.");
      }
    } finally {
      setLoading(false);
      setPageReady(true);
    }
  }, [occupantId, estateId, tenantId, isUnassignedParam]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const isUnassigned = isUnassignedParam || (!detail?.id && !loadError);

  const currentBuilding = useMemo(() => {
    if (detail?.currentAssignment) return detail.currentAssignment;
    if (targetBuildingId) {
      const found = assignedBuildings.find(b => String(b.id || b.buildingId) === String(targetBuildingId));
      if (found) return found;
    }
    return assignedBuildings[0] || null;
  }, [detail, targetBuildingId, assignedBuildings]);

  const openEditModal = () => {
    if (isUnassigned) {
      const defaultBId = targetBuildingId || assignedBuildings[0]?.id || assignedBuildings[0]?.buildingId || "";
      setEditForm({
        title: "",
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        gender: "",
        occupation: "",
        emergencyContact: "",
        buildingId: defaultBId,
      });
      setEditImageFile(null);
      setEditImagePreview(null);
    } else if (detail) {
      setEditForm({
        title: detail.title || "",
        firstName: detail.firstName || "",
        lastName: detail.lastName || "",
        email: detail.email || "",
        phoneNumber: detail.phoneNumber || "",
        gender: detail.gender || "",
        occupation: detail.occupation || "",
        emergencyContact: detail.emergencyContact || "",
        buildingId: detail.currentAssignment?.buildingId || targetBuildingId || "",
      });
      setEditImageFile(null);
      setEditImagePreview(detail.imageUrl || null);
    }
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditImageFile(null);
    setEditImagePreview(null);
    setEditModalOpen(false);
  };

  const handleAssignBuilding = async () => {
    if (!selectedBuildingId) return toast.error("Select a building.");
    setAssigning(true);
    try {
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${occupantId}/buildings/${selectedBuildingId}/assign`,
        { method: "POST", body: JSON.stringify({}) }
      );
      if (!res.ok) throw new Error((safeJsonParse(await res.text()))?.message || "Failed to assign building.");
      toast.success("Building assigned!");
      setSelectedBuildingId("");
      fetchAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignBuilding = async (buildingId) => {
    if (!(await confirmDialog({ title: "Unassign Building", message: "Unassign this building from the occupant?", confirmLabel: "Unassign" }))) return;
    setUnassigning(buildingId);
    try {
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${occupantId}/buildings/${buildingId}/unassign`,
        { method: "PATCH", body: JSON.stringify({}) }
      );
      if (!res.ok) throw new Error("Failed to unassign building.");
      toast.success("Building unassigned.");
      fetchAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUnassigning(null);
    }
  };

  const handleDeleteDependent = async (dependentId) => {
    if (!(await confirmDialog({ title: "Remove Dependent", message: "Remove this dependent? Their access will stop working.", confirmLabel: "Remove" }))) return;
    try {
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${occupantId}/dependents/${dependentId}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error("Failed to remove dependent.");
      toast.success("Dependent removed.");
      fetchAll();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const fetchDependentCodes = async (depId) => {
    setLoadingDependentCodes(true);
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${occupantId}/dependents/${depId}/access-codes?pageIndex=1&pageSize=20`);
      const j = safeJsonParse(await res.text());
      setDependentCodes(parseArrayData(j).map(normalizeAccessCode));
    } catch { setDependentCodes([]); }
    finally { setLoadingDependentCodes(false); }
  };

  const handleOpenDependentCodes = (dep) => {
    setSelectedDependentForCode(dep);
    fetchDependentCodes(dep.id);
  };

  const handleGenerateDependentCode = async () => {
    if (!selectedDependentForCode) return;
    setGeneratingCode(true);
    try {
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${occupantId}/dependents/${selectedDependentForCode.id}/access-codes`,
        { method: "POST", body: JSON.stringify({ isPermanent: codeIsPermanent, durationValue: Number(codeDurationValue), durationUnit: Number(codeDurationUnit) }) }
      );
      if (!res.ok) throw new Error("Failed to generate code.");
      toast.success("Access code generated!");
      fetchDependentCodes(selectedDependentForCode.id);
      fetchAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleDeactivateDependentCode = async (depId, codeId) => {
    if (!(await confirmDialog({ title: "Deactivate Access Code", message: "Deactivate this access code? It will stop working at the gate immediately.", confirmLabel: "Deactivate" }))) return;
    try {
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${occupantId}/dependents/${depId}/access-codes/${codeId}/deactivate`,
        { method: "PATCH" }
      );
      if (!res.ok) throw new Error("Failed to deactivate.");
      toast.success("Code deactivated.");
      fetchDependentCodes(depId);
      fetchAll();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.firstName?.trim() || !editForm.lastName?.trim()) {
      return toast.error("First Name and Last Name are required.");
    }

    setSavingEdit(true);
    try {
      let uploadedUrl = undefined;
      if (editImageFile) {
        const uploaded = await uploadAdminImage(editImageFile, getSession()?.userId);
        if (uploaded) uploadedUrl = uploaded;
      }

      if (isUnassigned) {
        // Add new primary occupant
        const chosenBuildingId = editForm.buildingId || targetBuildingId || assignedBuildings[0]?.id || assignedBuildings[0]?.buildingId;
        const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants`, {
          method: "POST",
          body: JSON.stringify({
            title: editForm.title || undefined,
            firstName: editForm.firstName.trim(),
            lastName: editForm.lastName.trim(),
            email: editForm.email?.trim() || undefined,
            phoneNumber: editForm.phoneNumber?.trim() || undefined,
            gender: editForm.gender || undefined,
            occupation: editForm.occupation?.trim() || undefined,
            emergencyContact: editForm.emergencyContact?.trim() || undefined,
            ...(uploadedUrl && { imageUrl: uploadedUrl }),
          }),
        });

        const resText = await res.text();
        const json = safeJsonParse(resText);
        if (!res.ok) throw new Error(json?.message || "Failed to create Primary Occupant.");

        const newOccupantId = typeof json?.data === "object" ? (json?.data?.id || json?.data?.primaryOccupantId) : json?.data;

        // Create ignores the title, but update accepts it, so set it straight after creating.
        if (newOccupantId && editForm.title) {
          const titleRes = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${newOccupantId}`, {
            method: "PUT",
            body: JSON.stringify({
              title: editForm.title,
              firstName: editForm.firstName.trim(),
              lastName: editForm.lastName.trim(),
              email: editForm.email?.trim() || "",
              phoneNumber: editForm.phoneNumber?.trim() || "",
              ...(uploadedUrl && { imageUrl: uploadedUrl }),
            }),
          });
          if (!titleRes.ok) toast.error("Occupant created, but the title couldn't be saved. Edit the occupant to add it.");
        }

        if (newOccupantId && chosenBuildingId) {
          const assignRes = await authFetch(
            `${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${newOccupantId}/buildings/${chosenBuildingId}/assign`,
            { method: "POST", body: JSON.stringify({}) }
          );
          if (!assignRes.ok) {
            toast.error("Occupant created, but failed to link to the building.");
          } else {
            toast.success("Primary Occupant added and assigned!");
          }
        } else {
          toast.success("Primary Occupant added!");
        }

        closeEditModal();
        if (newOccupantId) {
          navigate(`/tenants/${tenantId}/occupants/${newOccupantId}`);
        } else {
          fetchAll();
        }
      } else {
        // Edit existing primary occupant via PUT /estates/{estateId}/tenants/{tenantId}/primary-occupants/{id}
        const finalImageUrl = uploadedUrl !== undefined ? uploadedUrl : (editImagePreview ? (displayDetail?.imageUrl || "") : "");
        const payload = {
          primaryOccupantId: occupantId,
          id: occupantId,
          title: editForm.title?.trim() || "",
          firstName: editForm.firstName.trim(),
          lastName: editForm.lastName.trim(),
          email: editForm.email?.trim() || "",
          phoneNumber: editForm.phoneNumber?.trim() || "",
          gender: editForm.gender || undefined,
          occupation: editForm.occupation?.trim() || undefined,
          emergencyContact: editForm.emergencyContact?.trim() || undefined,
          ...(finalImageUrl ? { imageUrl: finalImageUrl } : {}),
        };

        // Admin route. PUT /primary-occupants/{id} is the occupant's own self-service endpoint.
        const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${occupantId}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });

        const resText = await res.text();
        const resJson = safeJsonParse(resText);
        if (!res.ok) throw new Error(resJson?.message || resJson?.detail || "Failed to update occupant.");

        toast.success("Occupant updated successfully.");
        closeEditModal();
        fetchAll();
      }
    } catch (err) {
      toast.error(err.message || "An error occurred.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleEditImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
    setEditImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setEditImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const displayDependents = useMemo(() => dependents, [dependents]);
  const displayCars = useMemo(() => cars, [cars]);
  const displayMaintenance = useMemo(() => maintenanceRequests, [maintenanceRequests]);
  const displayTenant = useMemo(() => tenantDetail || {}, [tenantDetail]);

  const displayDetail = detail;
  const fullName = isUnassigned
    ? "No Primary Occupant"
    : (toTitleCase(`${displayDetail?.firstName || ""} ${displayDetail?.lastName || ""}`.trim()) || "Primary Occupant");
  const initials = isUnassigned ? "?" : getInitials(fullName);
  const currentAssignment = displayDetail?.currentAssignment || currentBuilding;

  const tenantFullName = toTitleCase(`${displayTenant.title ? displayTenant.title + " " : ""}${displayTenant.firstName || ""} ${displayTenant.lastName || ""}`.trim()) || "Tenant";
  const tenantInitials = getInitials(`${displayTenant.firstName || ""} ${displayTenant.lastName || ""}`);
  const housesOwnedStr = (displayTenant.assignedBuildings || []).map(b => b.number || b.buildingNumber || b.name).filter(Boolean).join(", ") || (currentAssignment?.buildingNumber ? `Building ${currentAssignment.buildingNumber}` : "");

  const statusColor = (label) =>
    label === "Resolved" ? "#16A34A" : label === "Closed" ? "#a1a1aa" : label === "Open" ? "#DC2626" : "#D97706";
  const editBusy = savingEdit;

  return (
    <>
      <Dashboard6Styles />
      <PagePreloader visible={!pageReady || loading} />

      {/* ── Edit / Add Occupant Modal ── */}
      <InlineModal
        open={editModalOpen}
        icon="ki-user-square"
        title={isUnassigned ? "Add Primary Occupant" : "Edit Occupant"}
        onClose={() => { if (!editBusy) closeEditModal(); }}
        width={720}
        footer={
          <>
            <button type="button" className="btn btn-light d6-mbtn" onClick={closeEditModal} disabled={editBusy}>Cancel</button>
            <button type="button" className="btn btn-success d6-mbtn" onClick={handleSaveEdit} disabled={editBusy}>
              {editBusy ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>{isUnassigned ? "Adding…" : "Saving…"}</>
              ) : (
                isUnassigned ? "Add Primary Occupant" : "Save Changes"
              )}
            </button>
          </>
        }
      >
        <div className="d6m-upload">
          <button type="button" className="d6m-upload-preview" onClick={() => editFileInputRef.current?.click()} aria-label="Upload photo">
            {(editImagePreview || displayDetail?.imageUrl) ? <img src={editImagePreview || displayDetail?.imageUrl} alt="" /> : <i className="ki-outline ki-picture"></i>}
          </button>
          <div className="flex-grow-1 min-w-0">
            <div className="d6m-v">Profile photo</div>
            <div className="d6m-muted">
              {isUnassigned
                ? (currentBuilding ? `Assign to Building ${currentBuilding.number || currentBuilding.buildingNumber || ""}` : "PNG or JPG, up to 5MB")
                : `${fullName} · PNG or JPG, up to 5MB`}
            </div>
          </div>
          <div className="d-flex gap-2">
            {editImagePreview && (
              <button type="button" className="d6m-link-btn" onClick={() => { setEditImageFile(null); setEditImagePreview(null); }}>Remove</button>
            )}
            <button type="button" className="btn btn-light d6-mbtn d6-mbtn-sm" onClick={() => editFileInputRef.current?.click()}>
              {editImagePreview ? "Change" : "Upload"}
            </button>
          </div>
          <input ref={editFileInputRef} type="file" accept="image/*" className="d-none" onChange={handleEditImageSelect} />
        </div>

        <div className="d6m-section">
          <div className="d6m-section-head"><span>Personal information</span></div>
          <div className="row g-4">
            {isUnassigned && (
              <Field className="col-12" label="Assign to property" required>
                <select className="d6m-input" value={editForm.buildingId} onChange={e => setEditForm(p => ({ ...p, buildingId: e.target.value }))}>
                  <option value="">— Select Building —</option>
                  {assignedBuildings.map(b => (
                    <option key={b.id || b.buildingId} value={b.id || b.buildingId}>
                      {b.phaseName ? `${b.phaseName} - ` : ""}Building {b.number || b.buildingNumber} ({b.typeName || b.buildingTypeName || "Property"})
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field className="col-4" label="Title">
              <select className="d6m-input" value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}>
                <option value="">— Select —</option>
                {["Mr", "Mrs", "Ms", "Miss", "Dr", "Chief", "Engr", "Prof", "Alhaji", "Hajia"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field className="col-8" label="First name" required>
              <input className="d6m-input" placeholder="First Name" value={editForm.firstName} onChange={e => setEditForm(p => ({ ...p, firstName: e.target.value }))} />
            </Field>
            <Field className="col-12" label="Last name" required>
              <input className="d6m-input" placeholder="Last Name" value={editForm.lastName} onChange={e => setEditForm(p => ({ ...p, lastName: e.target.value }))} />
            </Field>
            <Field className="col-12" label="Email address">
              <input type="email" className="d6m-input" placeholder="email@example.com" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} />
            </Field>
            <Field className="col-12" label="Phone number">
              <input className="d6m-input" placeholder="+234 000 000 0000" value={editForm.phoneNumber} onChange={e => setEditForm(p => ({ ...p, phoneNumber: e.target.value }))} />
            </Field>
            {OCCUPANT_PROFILE_EXTRAS && <>
            <Field className="col-6" label="Gender">
              <select className="d6m-input" value={editForm.gender} onChange={e => setEditForm(p => ({ ...p, gender: e.target.value }))}>
                <option value="">— Select Gender —</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </Field>
            <Field className="col-6" label="Occupation">
              <input className="d6m-input" placeholder="e.g. Architect" value={editForm.occupation} onChange={e => setEditForm(p => ({ ...p, occupation: e.target.value }))} />
            </Field>
            <Field className="col-12" label="Emergency contact">
              <input className="d6m-input" placeholder="Contact name and phone" value={editForm.emergencyContact} onChange={e => setEditForm(p => ({ ...p, emergencyContact: e.target.value }))} />
            </Field>
            </>}
          </div>
        </div>
      </InlineModal>

      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div id="kt_app_header" className="app-header" data-kt-sticky="true" style={{ position: "sticky", top: 0, zIndex: 100 }}>
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <Link to="/tenants"><img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" /></Link>
              </div>
              <div className="d-flex flex-stack flex-lg-row-fluid">
                <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3 mb-0">
                  <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 mb-2">
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1">
                      <Link to="/dashboard" className="text-gray-700 text-hover-primary me-1"><i className="ki-outline ki-home text-gray-700 fs-6"></i></Link>
                    </li>
                    <li className="breadcrumb-item"><i className="ki-outline ki-right fs-7 text-gray-700 mx-n1"></i></li>
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1"><Link to="/tenants" className="text-gray-700 text-hover-primary">Tenants</Link></li>
                    <li className="breadcrumb-item"><i className="ki-outline ki-right fs-7 text-gray-700 mx-n1"></i></li>
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1"><Link to={`/tenants/${tenantId}`} className="text-gray-700 text-hover-primary">Tenant Profile</Link></li>
                    <li className="breadcrumb-item"><i className="ki-outline ki-right fs-7 text-gray-700 mx-n1"></i></li>
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1">{isUnassigned ? "Add Primary Occupant" : fullName}</li>
                  </ul>
                  <h1 className="text-gray-900 d6-page-title m-0">Primary Occupant</h1>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <Link to={`/tenants/${tenantId}`} className="btn btn-light d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold">
                    <i className="ki-outline ki-arrow-left fs-6 me-sm-2"></i><span className="d-none d-sm-inline">Back to Tenant</span>
                  </Link>
                  {isUnassigned ? (
                    <button type="button" className="btn btn-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={openEditModal}>
                      <i className="ki-outline ki-plus fs-6 me-sm-2 text-white"></i><span className="d-none d-sm-inline">Add Primary Occupant</span>
                    </button>
                  ) : (
                    <button type="button" className="btn btn-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={openEditModal}>
                      <i className="ki-outline ki-pencil fs-6 me-sm-2 text-white"></i><span className="d-none d-sm-inline">Edit Occupant</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />
            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">
                    {loadError && !detail ? (
                      <div className="d6-card p-10 text-center">
                        <i className="ki-outline ki-information-5 fs-3x d-block mb-4" style={{ color: "#dc2626" }} />
                        <div className="fw-bold text-gray-900 fs-5 mb-2">{loadError}</div>
                        <div className="text-gray-500 fs-7 mb-6">Nothing has been changed.</div>
                        <button type="button" className="btn btn-success d6-mbtn" onClick={fetchAll} disabled={loading}>
                          {loading ? <span className="spinner-border spinner-border-sm" /> : "Retry"}
                        </button>
                      </div>
                    ) : (
                    <div className="row g-5">

                      {/* LEFT COLUMN — Profile, Tenant Detail & Assigned House */}
                      <div className="col-12 col-xl-4">
                        <div className="d6-card p-6 mb-5" style={{ position: "sticky", top: "90px" }}>
                          <div className="d6m-person mb-5">
                            {isUnassigned ? (
                              <span className="d6m-tile" style={{ width: 64, height: 64, borderRadius: 999 }}><i className="ki-outline ki-user" /></span>
                            ) : (
                              <Avatar src={displayDetail?.imageUrl} name={fullName} size={64} />
                            )}
                            <div className="min-w-0 flex-grow-1">
                              <div className="d6m-name fs-5 text-truncate">{fullName}</div>
                              <div className="mt-1">
                                <StatusChip color={isUnassigned ? "#D97706" : "#16A34A"}>{isUnassigned ? "Unassigned" : "Primary Occupant"}</StatusChip>
                              </div>
                            </div>
                          </div>

                          {isUnassigned ? (
                            <div className="d6m-note mt-0 mb-5" style={{ background: "#fffbeb", color: "#92400e" }}>
                              <i className="ki-outline ki-information-5" style={{ color: "#d97706" }}></i>
                              <span className="flex-grow-1">
                                <strong>House not assigned.</strong> This property has no primary occupant yet. Add one to manage dependants, vehicles and access codes.
                                <button type="button" className="btn btn-success d6-mbtn w-100 mt-3" onClick={openEditModal}>
                                  <i className="ki-outline ki-plus fs-6 me-2 text-white"></i>Add Primary Occupant
                                </button>
                              </span>
                            </div>
                          ) : (
                            <div className="d6m-kv mb-5" style={{ gridTemplateColumns: "minmax(0, 1fr)" }}>
                              {[
                                displayDetail?.phoneNumber && displayDetail?.phoneNumber !== "—" ? ["Phone", displayDetail.phoneNumber] : null,
                                displayDetail?.email && displayDetail?.email !== "—" ? ["Email", displayDetail.email] : null,
                                OCCUPANT_PROFILE_EXTRAS ? ["Gender", displayDetail?.gender || "—"] : null,
                                OCCUPANT_PROFILE_EXTRAS ? ["Occupation", displayDetail?.occupation || "—"] : null,
                                OCCUPANT_PROFILE_EXTRAS ? ["Emergency contact", displayDetail?.emergencyContact || "—"] : null,
                                ["Date created", fmtDateLong(displayDetail?.createdDate || displayDetail?.createdAt)],
                              ].filter(Boolean).map(([k, v], idx) => (
                                <div key={k} className="d6m-kv-cell d-flex align-items-center justify-content-between gap-3" style={idx > 0 ? { borderLeft: 0, borderTop: "1px solid #ececef" } : undefined}>
                                  <span className="d6m-k mb-0">{k}</span>
                                  <span className="d6m-v text-end text-break">{v}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {(displayTenant?.firstName || displayTenant?.lastName) && (
                            <div className="mb-5">
                              <div className="d6m-section-head"><span>Tenant detail</span></div>
                              <div className="d6m-property">
                                <Avatar src={displayTenant?.imageUrl} name={tenantFullName} size={48} />
                                <div className="min-w-0 flex-grow-1">
                                  <div className="d6m-v text-truncate">{tenantFullName}</div>
                                  {displayTenant?.phoneNumber && <div className="d6m-muted">{displayTenant.phoneNumber}</div>}
                                  <div className="d6m-muted text-truncate">
                                    {[displayTenant.tenantType || "Resident", housesOwnedStr].filter(Boolean).join(" · ")}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="d6m-section-head"><span>Assigned house</span></div>

                          {currentAssignment ? (() => {
                            const bNumStr = String(currentAssignment.buildingNumber || "").trim();
                            const phaseName = currentAssignment.phaseName || "";
                            const typeName = currentAssignment.typeName || "Assigned House";
                            const houseImg = currentAssignment.imageUrl || getHouseImage(typeName);
                            const rentVal = Number(currentAssignment.rentAmount || currentAssignment.basicRent || 0);
                            const serviceCharge = Number(currentAssignment.serviceCharge || 0);
                            const assignedOn = currentAssignment.assignedAt || currentAssignment.assignedDate;
                            const isDueHouse = !!(currentAssignment.isRentDue || currentAssignment.isDue);

                            return (
                              <div className={`d6-card d-flex flex-column ${isDueHouse ? "border-hover-danger" : "border-hover-success"} transition-all overflow-hidden`}>
                                <div className="position-relative overflow-hidden" style={{ height: 180 }}>
                                  <img
                                    alt={typeName}
                                    src={houseImg}
                                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                    onError={(e) => { e.target.src = "/assets/media/zangon/6bedroombungalow.jpg"; }}
                                  />
                                  <div className="position-absolute" style={{ inset: 0, background: "linear-gradient(rgba(0,0,0,0) 50%, rgba(0,0,0,0.65) 100%)" }} />
                                  <div className="position-absolute bottom-0 start-0 p-3 d-flex align-items-center gap-2">
                                    {bNumStr && <span className="badge bg-white text-gray-900 border border-gray-200 fs-8 fw-bold rounded-pill px-3 py-1">{bNumStr}</span>}
                                    {phaseName && <span className="badge bg-success text-white fs-8 fw-semibold rounded-pill px-3 py-1">{phaseName}</span>}
                                  </div>
                                </div>
                                <div className="p-4 d-flex flex-column flex-grow-1">
                                  <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                                    <div className="min-w-0">
                                      <h4 className="d6-title text-gray-900 mb-1 text-truncate">{typeName}</h4>
                                      <span className="d6m-muted fs-8">{assignedOn ? `Assigned ${fmtDate(assignedOn)}` : "Assigned house"}</span>
                                    </div>
                                    <StatusChip color={isDueHouse ? "#DC2626" : "#16A34A"}>{isDueHouse ? "Due" : "Active"}</StatusChip>
                                  </div>
                                  <div className="d-flex align-items-center justify-content-between mt-auto pt-3 border-top border-gray-100">
                                    <div className="d-flex flex-column min-w-0">
                                      <span className="d6m-k mb-0 fs-9">Base Rent</span>
                                      <div className="d-flex align-items-baseline gap-2">
                                        <span className="d6-title text-success fs-7 mb-0">₦{rentVal.toLocaleString()}</span>
                                        {serviceCharge > 0 && <span className="text-gray-400 fs-9 text-truncate">+₦{serviceCharge.toLocaleString()} Service Charge</span>}
                                      </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      {isUnassigned ? (
                                        <button type="button" className="d6-icon-btn" onClick={openEditModal} title="Add Primary Occupant">
                                          <i className="ki-outline ki-plus fs-5 text-gray-700" />
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          className="d6-icon-btn"
                                          onClick={() => handleUnassignBuilding(currentAssignment.buildingId)}
                                          disabled={unassigning === currentAssignment.buildingId}
                                          title="Unassign Building"
                                        >
                                          {unassigning === currentAssignment.buildingId ? <span className="spinner-border spinner-border-sm" /> : <i className="ki-outline ki-trash fs-5 text-danger" />}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })() : (
                            <div className="d6m-invoice">
                              <div className="d6m-muted mb-3">Not assigned to a property.</div>
                              <div className="d-flex flex-column gap-3">
                                <select className="d6m-input" value={selectedBuildingId} onChange={e => setSelectedBuildingId(e.target.value)}>
                                  <option value="">— Choose a building —</option>
                                  {assignedBuildings.map(b => (
                                    <option key={b.id || b.buildingId} value={b.id || b.buildingId}>
                                      {b.phaseName ? `${b.phaseName} - ` : ""}{b.number}
                                    </option>
                                  ))}
                                </select>
                                <button type="button" className="btn btn-success d6-mbtn w-100" onClick={handleAssignBuilding} disabled={assigning || !selectedBuildingId}>
                                  {assigning ? <><span className="spinner-border spinner-border-sm me-2"></span>Assigning…</> : "Assign Building"}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* RIGHT COLUMN — Activity Sections */}
                      <div className="col-12 col-xl-8">
                        <div className="d-flex flex-column h-100">

                          {/* DEPENDENTS */}
                          <div className="d6-card p-6 mb-5">
                            <CardHead title={`Dependants (${displayDependents.length})`} subtitle="Registered family members and staff" className="mb-4">
                              {selectedDependentForCode && (
                                <button type="button" className="btn btn-light d6-mbtn" onClick={() => { setSelectedDependentForCode(null); setDependentCodes([]); }}>
                                  <i className="ki-outline ki-arrow-left fs-6 me-2"></i>Back to Dependents
                                </button>
                              )}
                            </CardHead>

                            {selectedDependentForCode ? (
                              <div className="d-flex flex-column gap-5">
                                <div className="d6m-invoice">
                                  <div className="d6m-person mb-4">
                                    <Avatar src={selectedDependentForCode.imageUrl} name={`${selectedDependentForCode.firstName || ""} ${selectedDependentForCode.lastName || ""}`} size={48} />
                                    <div className="min-w-0 flex-grow-1">
                                      <div className="d6m-name fs-6 text-truncate">{toTitleCase(`${selectedDependentForCode.firstName || ""} ${selectedDependentForCode.lastName || ""}`.trim())}</div>
                                      <div className="d6m-muted">{formatDependentType(selectedDependentForCode.dependentType)}</div>
                                    </div>
                                  </div>
                                  <div className="d6m-section-head"><span>Generate access code</span></div>
                                  <div className="d6m-seg mb-3">
                                    <button type="button" className={codeIsPermanent ? "active" : ""} onClick={() => setCodeIsPermanent(true)}>Permanent</button>
                                    <button type="button" className={!codeIsPermanent ? "active" : ""} onClick={() => setCodeIsPermanent(false)}>Temporary</button>
                                  </div>
                                  {!codeIsPermanent && (
                                    <div className="row g-3 mb-3">
                                      <Field className="col-6" label="Value">
                                        <input type="number" className="d6m-input" value={codeDurationValue} min="1" onChange={e => setCodeDurationValue(e.target.value)} />
                                      </Field>
                                      <Field className="col-6" label="Unit">
                                        <select className="d6m-input" value={codeDurationUnit} onChange={e => setCodeDurationUnit(Number(e.target.value))}>
                                          <option value={0}>Hours</option>
                                          <option value={1}>Days</option>
                                          <option value={2}>Weeks</option>
                                          <option value={3}>Months</option>
                                        </select>
                                      </Field>
                                    </div>
                                  )}
                                  <button type="button" className="btn btn-success d6-mbtn w-100" onClick={handleGenerateDependentCode} disabled={generatingCode}>
                                    {generatingCode ? <><span className="spinner-border spinner-border-sm me-2"></span>Generating…</> : "Generate Code"}
                                  </button>
                                </div>

                                <div>
                                  <div className="d6m-section-head"><span>Code history</span></div>
                                  {loadingDependentCodes ? (
                                    <div className="d6m-empty"><span className="spinner-border spinner-border-sm text-success me-2"></span>Loading codes...</div>
                                  ) : dependentCodes.length === 0 ? (
                                    <div className="d6m-empty">No codes found for this dependent.</div>
                                  ) : (
                                    <div className="d6m-list">
                                      {dependentCodes.map((c, i) => (
                                        <div key={i} className="d6m-list-row" style={{ cursor: "default" }}>
                                          <span className="min-w-0 flex-grow-1 text-start">
                                            <span className="d6m-v d-block fs-5">{c.code}</span>
                                            <span className="d6m-muted d-block">Issued {fmtDateTime(c.issuedAt)}</span>
                                          </span>
                                          <StatusChip color={c.isPermanent ? "#16A34A" : "#D97706"}>{c.isPermanent ? "Permanent" : `${c.durationValue} ${durationUnitLabel(c.durationUnit)}`}</StatusChip>
                                          <StatusChip color={c.isActive ? "#16A34A" : "#a1a1aa"}>{c.isActive ? "Active" : "Inactive"}</StatusChip>
                                          {c.isActive && (
                                            <button type="button" className="btn btn-light-danger d6-mbtn d6-mbtn-sm" onClick={() => handleDeactivateDependentCode(selectedDependentForCode.id, c.id)}>
                                              Deactivate
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : displayDependents.length === 0 ? (
                              <div className="d6m-empty py-12">
                                <i className="ki-outline ki-people fs-3x text-gray-300 d-block mb-3"></i>
                                <div className="d6-title text-gray-900 mb-1">No dependants registered.</div>
                                <div className="fs-8 text-gray-500">{isUnassigned ? "Family members and staff will appear here once an occupant is assigned." : "Family members and staff will appear here."}</div>
                              </div>
                            ) : (
                              <div className="d-flex flex-column gap-3">
                                {displayDependents.map((dep, i) => {
                                  const name = toTitleCase(`${dep.firstName || ""} ${dep.lastName || ""}`.trim()) || "Dependant";
                                  const roleTag = formatDependentType(dep.dependentType);
                                  const isPermanentAccess = dep.dependentType === "Staff" || dep.dependentType === "FamilyMember" || dep.dependentType === 0 || dep.dependentType === 1;
                                  return (
                                    <div key={dep.id || i} className="d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 border-hover-success position-relative overflow-hidden transition-all" style={{ minHeight: 84 }}>
                                      <div className="d-flex align-items-center me-4" style={{ width: "340px", flexShrink: 0 }}>
                                        <Avatar src={dep.imageUrl} name={name} size={48} />
                                        <div className="d-flex flex-column overflow-hidden min-w-0 ms-3">
                                          <div className="d-flex align-items-center gap-2 mb-0.5 flex-wrap">
                                            <span className="fs-6 fw-bold text-gray-900 text-truncate">{name}</span>
                                            <span className={`badge fs-9 fw-semibold rounded-pill px-2.5 py-0.5 ${isPermanentAccess ? "bg-light-success text-success" : "bg-light-primary text-primary"}`}>{roleTag}</span>
                                          </div>
                                          <div className="text-gray-500 fs-8 text-truncate">{dep.phoneNumber || "No phone"}</div>
                                        </div>
                                      </div>
                                      <div className="d-flex align-items-center gap-2 ms-auto flex-shrink-0">
                                        <button type="button" className="btn btn-light d6-mbtn d6-mbtn-sm" onClick={() => handleOpenDependentCodes(dep)}>
                                          <i className="ki-outline ki-key fs-6 me-2"></i>Codes
                                        </button>
                                        <button type="button" className="d6-icon-btn" onClick={() => handleDeleteDependent(dep.id)} title="Remove Dependent">
                                          <i className="ki-outline ki-trash fs-5 text-danger"></i>
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* CARS & DRIVERS */}
                          <div className="d6-card p-6 mb-5">
                            <CardHead title={`Cars & Drivers (${displayCars.length})`} subtitle="Registered vehicles and driver details" className="mb-4" />
                            {displayCars.length === 0 ? (
                              <div className="d6m-empty py-12">
                                <i className="ki-outline ki-car fs-3x text-gray-300 d-block mb-3"></i>
                                <div className="d6-title text-gray-900 mb-1">No registered vehicles found.</div>
                                <div className="fs-8 text-gray-500">{isUnassigned ? "Cars and driver details will appear here once an occupant is assigned." : "Cars and driver details will appear here once registered."}</div>
                              </div>
                            ) : (
                              <div className="row g-5">
                                {displayCars.map((car, i) => {
                                  const makeStr = car.make || car.manufacturer || "";
                                  const modelStr = car.model || "";
                                  const carName = car.manufacturer && car.make && car.manufacturer !== car.make
                                    ? `${car.manufacturer} ${car.make}`
                                    : (car.make && car.model && car.make !== car.model
                                        ? `${car.make} ${car.model}`
                                        : (car.manufacturer || car.make || car.model || "Vehicle"));
                                  const imgUrl = car.imageUrl || getCarImageUrl(makeStr, modelStr, car.year, car.color);
                                  const plate = car.licensePlate || "No Plate";
                                  const driversCount = car.assignedDependentsCount || car.driversCount || 0;
                                  const drivers = Array.isArray(car.assignedDependents) ? car.assignedDependents : [];

                                  return (
                                    <div key={car.id || i} className="col-12 col-md-6">
                                      <div className="d6-card h-100 d-flex flex-column border-hover-success transition-all p-5 position-relative overflow-hidden">
                                        <div className="position-relative rounded-3 bg-light overflow-hidden d-flex align-items-center justify-content-center border border-gray-200 mb-4" style={{ height: 160 }}>
                                          {imgUrl ? (
                                            <img
                                              alt={carName}
                                              src={imgUrl}
                                              style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", padding: 10 }}
                                              onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; }}
                                            />
                                          ) : (
                                            <i className="ki-outline ki-car fs-2x text-gray-400"></i>
                                          )}
                                          <div className="position-absolute bottom-0 start-0 p-3">
                                            <span className="badge bg-white text-gray-900 border border-gray-200 fs-8 fw-bold rounded-pill px-3 py-1">{plate}</span>
                                          </div>
                                        </div>

                                        <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                                          <div className="min-w-0">
                                            <h4 className="d6-title text-gray-900 mb-1 text-truncate">{carName}</h4>
                                            <span className="d6m-muted fs-8">{[car.year, car.color].filter(Boolean).join(" • ") || "Registered vehicle"}</span>
                                          </div>
                                          <StatusChip color="#0EA84A">Registered</StatusChip>
                                        </div>

                                        <div className="p-3 bg-light rounded-3 mt-auto">
                                          <div className="d-flex align-items-center gap-3">
                                            <div className="d-flex align-items-center flex-shrink-0">
                                              {drivers.length === 0 ? (
                                                <span className="d6m-tile" style={{ width: 36, height: 36, borderRadius: 999 }}><i className="ki-outline ki-user" /></span>
                                              ) : (
                                                drivers.slice(0, 4).map((dr, di) => (
                                                  <span key={dr.dependentId || di} title={dr.fullName} style={{ marginLeft: di > 0 ? -10 : 0, zIndex: 10 - di, borderRadius: "50%", border: "2px solid #f4f4f5", display: "inline-flex" }}>
                                                    <Avatar src={dr.imageUrl} name={dr.fullName} size={36} />
                                                  </span>
                                                ))
                                              )}
                                            </div>
                                            <div className="d-flex flex-column min-w-0">
                                              <span className="d6m-name fs-8 text-truncate">
                                                {drivers.length > 0 ? drivers.map(dr => dr.fullName).join(", ") : "No drivers linked"}
                                              </span>
                                              <span className="d6m-muted fs-9">{driversCount || drivers.length} driver(s) assigned</span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* MAINTENANCE REQUESTS */}
                          <div className="d6-card p-6 mb-5 d-flex flex-column flex-grow-1">
                            <CardHead title={`Maintenance Requests (${displayMaintenance.length})`} subtitle="Service and repair logs" className="mb-4" />
                            {displayMaintenance.length === 0 ? (
                              <div className="d6m-empty py-12 flex-grow-1 d-flex flex-column align-items-center justify-content-center">
                                <i className="ki-outline ki-wrench fs-3x text-gray-300 d-block mb-3"></i>
                                <div className="d6-title text-gray-900 mb-1">You are all caught up.</div>
                                <div className="fs-8 text-gray-500">{isUnassigned ? "No maintenance requests found for this property." : "No maintenance requests found for this occupant."}</div>
                              </div>
                            ) : (
                              <div className="d-flex flex-column gap-3">
                                {displayMaintenance.map((r, i) => {
                                  const statusMeta = STATUS_META[r.status] || STATUS_META["Open"];
                                  const isResolved = r.status === "Resolved" || r.status === "Closed";
                                  return (
                                    <div
                                      key={r.id || i}
                                      className={`d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 ${isResolved ? "border-hover-success" : "border-hover-warning"} position-relative overflow-hidden transition-all`}
                                      style={{ minHeight: 84 }}
                                    >
                                      <div className={`position-absolute top-0 start-0 h-100 ${isResolved ? "bg-success" : "bg-warning"} w-4px`}></div>
                                      <span className="d6m-tile me-3 flex-shrink-0" style={{ width: 44, height: 44, borderRadius: 999 }}><i className="ki-outline ki-wrench" /></span>
                                      <div className="d-flex flex-column overflow-hidden me-4 min-w-0 flex-grow-1">
                                        <div className="d-flex align-items-center gap-2 mb-0.5 flex-wrap">
                                          <span className="fs-6 fw-bold text-gray-900 text-truncate">{r.title || r.shortDescription || "Maintenance Request"}</span>
                                          <span className="badge bg-light text-gray-700 fs-9 fw-semibold rounded-pill px-2.5 py-0.5">{r.category || "General"}</span>
                                        </div>
                                        <div className="text-gray-500 fs-8 text-truncate">Building {r.buildingNumber || "—"}</div>
                                      </div>
                                      <div className="d-flex align-items-center gap-5 text-end ms-auto flex-shrink-0">
                                        <div className="d-none d-sm-block text-center">
                                          <div className="text-gray-400 fs-9 mb-0.5">Requested</div>
                                          <div className="fw-semibold text-gray-900 fs-8">{fmtDate(r.createdDate || r.createdAt)}</div>
                                        </div>
                                        <StatusChip color={statusColor(statusMeta.label)}>{statusMeta.label}</StatusChip>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                        </div>
                      </div>

                    </div>
                    )}

                    <div className="my-10"></div>
                  </div>
                </div>
              </div>

              <div id="kt_app_footer" className="app-footer">
                <div className="app-container container-fluid d-flex justify-content-center align-items-center py-3">
                  <div className="text-gray-900 text-center">
                    <span className="text-muted fw-semibold me-1">2026&copy;</span>
                    <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary">Powered by Pixels Solutions Limited</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="kt_scrolltop" className="scrolltop bg-success" data-kt-scrolltop="true">
            <i className="ki-outline ki-arrow-up"></i>
          </div>
        </div>
      </div>
    </>
  );
}
